import React from "react";
import { Button, Link } from "@material-ui/core";
import DataTable from "@/components/DataTable";
import { ReactRender } from "@/components/ReactRender";
import { useTranslation } from "@/langs/useTranslation";
import { getNamingList } from "@/apis/apiCollection";
import HasPermission from "@/components/commons/HasPermission";
import { formatDate } from "./datetimeHandlers";
import { DeviceNamingChipStatus } from "@/components/FormFields/DeviceNamingStatus";
import { OSTypeChip } from "@/components/FormFields/OSType";
import { useRuleList } from "@/containers/Setting/CreateNaming/NamingCreator";

export const Rule = ({ rules, delimiter }) => {
  if (!rules) rules = [];
  if (typeof rules === "string") {
    rules = JSON.parse(rules);
  }
  const list = useRuleList();
  const keyMap = {};
  list.forEach(item => {
    keyMap[item.id] = item.name;
  });
  return rules.map(item => keyMap[item.type]).join(delimiter);
};

export const DeviceNamingDatatable = React.forwardRef(
  (
    {
      className,
      style,
      getSearchParams,
      footer,
      children,
      onCheckAll,
      onLoaded,
      onView,
      onEdit,
      onAssociated,
      onEnable,
      onDelete,
      onViewMerchants
    },
    ref
  ) => {
    const { t } = useTranslation();

    const columns = [
      {
        data: "id",
        type: "checkbox"
      },
      {
        name: "orderNo",
        title: t({ defaultMessage: "序号" })
      },
      {
        data: "name",
        title: t({ defaultMessage: "命名规则名称" })
      },
      {
        data: "osType",
        title: t({ defaultMessage: "OS类型" }),
        createdCell: (td, data) => {
          ReactRender(<OSTypeChip type={data} />, td);
        }
      },
      {
        data: "status",
        title: t({ defaultMessage: "状态" }),
        className: "center",
        createdCell: (td, data) => {
          ReactRender(<DeviceNamingChipStatus status={data} />, td);
        }
      },
      {
        data: "groupNum",
        title: t({ defaultMessage: "套用门店数" }),
        className: "center",
        createdCell: (td, data, row) => {
          ReactRender(
            <>
              {data > 0 ? (
                <Link color="primary" onClick={() => onViewMerchants(row)}>
                  {data}
                </Link>
              ) : (
                data
              )}
            </>,
            td
          );
        }
      },
      {
        data: "rules",
        title: t({ defaultMessage: "命名规则" }),
        createdCell: (td, data, row) => {
          ReactRender(<Rule rules={data} delimiter={row.delimiter} />, td);
        }
      },
      {
        data: "createdAt",
        title: t({ defaultMessage: "创建时间" }),
        render: data => {
          return formatDate(data);
        }
      },
      {
        data: "updatedAt",
        title: t({ defaultMessage: "最后更新时间" }),
        render: data => {
          return formatDate(data);
        }
      },
      {
        data: null,
        title: t({ defaultMessage: "操作" }),
        searchable: false,
        orderable: false,
        width: 48,
        createdCell: (td, data, row) => {
          ReactRender(
            <>
              <Button size="small" color="primary" onClick={() => onView(row)}>
                {t({ defaultMessage: "查看" })}
              </Button>
              <HasPermission permission="device_naming_method_edit">
                <Button
                  size="small"
                  color="primary"
                  onClick={() => onEdit(row)}
                >
                  {t({ defaultMessage: "编辑" })}
                </Button>
              </HasPermission>
              <HasPermission permission="device_naming_method_associated_with_merchant">
                <Button
                  size="small"
                  color="primary"
                  onClick={() => onAssociated(row)}
                >
                  {t({ defaultMessage: "套用门店" })}
                </Button>
              </HasPermission>
              <HasPermission permission="device_naming_method_enable_disable">
                {row.status === 0 ? (
                  <Button
                    size="small"
                    color="primary"
                    onClick={() => onEnable(row, false)}
                  >
                    {t({ defaultMessage: "禁用" })}
                  </Button>
                ) : (
                  <Button
                    size="small"
                    color="primary"
                    onClick={() => onEnable(row, true)}
                  >
                    {t({ defaultMessage: "启用" })}
                  </Button>
                )}
              </HasPermission>
              <HasPermission permission="device_naming_method_delete">
                <Button
                  size="small"
                  color="secondary"
                  onClick={() => onDelete(row)}
                >
                  {t({ defaultMessage: "删除" })}
                </Button>
              </HasPermission>
            </>,
            td
          );
        }
      }
    ];

    const ajax = (data, callback) => {
      let orderType;
      let orderWay;
      const order = data.order[0];
      if (order) {
        // orderType = data.columns[order.column]?.name;
        orderType = order.column;
        orderWay = order.dir === "asc" ? 1 : 2;
      }
      return getNamingList({
        // ...data,
        orderType,
        orderWay,
        ...getSearchParams(),
        page: data.page,
        pageSize: data.pageSize
      }).then(res => {
        callback({
          data: res.namingRules,
          draw: data.draw,
          recordsFiltered: res.total,
          recordsTotal: res.total
        });
      });
    };

    return (
      <DataTable
        ref={ref}
        ordering={false}
        paging={false}
        options={{ order: [[1, "asc"]] }}
        columns={columns}
        ajax={ajax}
        className={className}
        style={style}
        footer={footer}
        scrollable
        showCheckAll={false}
        onCheckAll={onCheckAll}
        onLoaded={onLoaded}
      >
        {children}
      </DataTable>
    );
  }
);
